import { Fragment, ReactNode } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { LinkProps } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';

import { GlobalNavDropdownLinkColumn } from './layouts/global-nav-dropdown-link-column';
import { DivOnHoverExit } from './nav-links-container';

export interface DropdownContentProps {
	contentSections: DropdownContentItem[];
	orientation?: 'column' | 'row';
}

export type DropdownContentItem =
	| DropdownContentSectionProps
	| {
			divider: ReactNode;
	  };

export const DropdownContent = (props: DropdownContentProps) => {
	const { contentSections } = props;
	const { orientation = 'row' } = props;
	const hasAuthoredDividers = contentSections.some((section) => 'divider' in section);

	return (
		<DivOnHoverExit>
			<div
				css={[
					orientation === 'row' && dropdownGridStyle,
					orientation === 'column' && dropdownColumnStyles,
				]}
			>
				{contentSections.map((section, idx) => (
					<Fragment key={idx}>
						{'divider' in section ? section.divider : <DropdownContentSection {...section} />}
						{orientation === 'column' &&
							!hasAuthoredDividers &&
							idx < contentSections.length - 1 && <div css={dropdownColumnDividerStyles} />}
					</Fragment>
				))}
			</div>
		</DivOnHoverExit>
	);
};

export interface DropdownContentSectionProps {
	header?: string;
	links: LinkProps[];
}

const DropdownContentSection = ({ header, links }: DropdownContentSectionProps) => {
	const { isDarkTheme } = useTheme();

	return (
		<GlobalNavDropdownLinkColumn>
			{header && (
				<h2 className="nav-column-header" css={[headerStyle, isDarkTheme && headerDarkThemeStyle]}>
					{header}
				</h2>
			)}
			<ul css={linkColumnStyle} role="menu">
				{links.map((link) => (
					<li key={link.text} role="none">
						<a
							role="menuitem"
							href={link.href}
							{...link.dataEventProps}
							data-event="clicked"
							data-event-component="linkButton"
							data-event-container="global-Product-Nav-link-in-dropdown"
							data-label={link.text}
							css={[linkStyle, isDarkTheme && linkDarkThemeStyle]}
							onClick={link.onClick}
							{...link.anchorProps}
						>
							{link.text}
						</a>
					</li>
				))}
			</ul>
		</GlobalNavDropdownLinkColumn>
	);
};

const dropdownGridStyle = css({
	padding: token('space.500'),
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	gap: '32px',
	[media.above.md]: {
		flexDirection: 'row',
	},
});

const dropdownColumnStyles = css({
	padding: token('space.500'),
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	gap: '16px',
});

const dropdownColumnDividerStyles = css({
	width: '100%',
	borderBlockStart: `solid 1px ${wacToken('color.neutral.dark1000')}`,
});

const headerStyle = css({
	textTransform: 'uppercase',
	color: wacToken('color.primary.black'),
	fontSize: '0.75rem',
	fontFamily: wacToken('font.charlie.text'),
	fontWeight: 700,
	lineHeight: '1rem',
});

const headerDarkThemeStyle = css({
	color: wacToken('color.neutral.light0'),
});

const linkColumnStyle = css({
	display: 'grid',
	rowGap: token('space.250'),
	width: '184px',
	listStyle: 'none',
	margin: 0,
	padding: 0,
});

const linkStyle = css({
	margin: 0,
	color: wacToken('color.primary.black'),
	fontSize: '.875rem',
	alignSelf: 'end',
	':hover': {
		color: wacToken('color.extended.blue700'),
	},
});

const linkDarkThemeStyle = css({
	color: wacToken('color.neutral.light0'),
	':hover': {
		color: wacToken('color.extended.blue200'),
	},
});
